import { TranslateService } from '@ngx-translate/core';

import { CoachingEventStatus } from '../../coaching-base/general';
import { sortOpportunities } from '../../coaching-base/opportunity/sort-opportunities.function';
import { epicDependencies } from '../../store';
import { TranslationKeyMap } from '../coaching-card-rep.i18n';
import { CardItemType } from '../enums/card-item-type.enum';
import { compare } from '../helper/sortable-table-header.directive';
import { AccordionItem } from '../models/accordion-item.model';
import { CoachingType } from '../models/coaching-type.model';
import { RepCard } from '../models/rep-card.model';

import { Opportunity } from './../../coaching-base/general/opportunity.model';
import { goalToOppTransformer } from './goal-to-opp-transformer.function';
import { overrideActiveDate } from './override-active-date-for-opps.function';

export function cardDataTransformer(card: RepCard): AccordionItem[] {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const goals = card.goals;

    const coached = goals?.filter(
        (e) =>
            (e.statusId === CoachingEventStatus.ACTIVE &&
                e.isOriginalCommitment === true &&
                e.approvalId == null) ||
            (e.statusId === CoachingEventStatus.DISPUTED &&
                e.commitmentId != null &&
                e.isOriginalCommitment == null &&
                e.approvalId != null),
    );
    const unCoached = goals?.filter(
        (e) =>
            e.coachTypeId === CoachingType.UNCOACHED &&
            e.statusId !== CoachingEventStatus.UPDATING,
    );
    const pendingCommitment = goals?.filter(
        (e) =>
            e.statusId === CoachingEventStatus.UPDATING ||
            e.statusId === CoachingEventStatus.PRE_APPROVAL ||
            e.statusId === CoachingEventStatus.PENDING_CLOSE,
    );

    const items: AccordionItem[] = [];

    [...Array(3)].forEach((_, index) => {
        const item: AccordionItem = {};
        item.title = translateService.instant(
            CardItemTypeTranslationMap[index],
        );

        if (index === CardItemType.PENDING_COMMITMENTS) {
            const childItems = goalToOppTransformer(
                pendingCommitment,
                CardItemType.PENDING_COMMITMENTS,
            );
            childItems?.sort((a, b) => {
                const res = compare(
                    a.opportunityItem.lastUpdated,
                    b.opportunityItem.lastUpdated,
                );
                return res;
            });
            item.isOpened = pendingCommitment?.length > 0;
            item.isTableExpanded = false;
            item.isVisible = pendingCommitment?.length > 0;
            item.type = CardItemType.PENDING_COMMITMENTS;
            item.isShowMoreEnabled = pendingCommitment?.length > 3;
            item.hasShowMore = pendingCommitment?.length > 3;
            item.childItems = childItems;
        } else if (index === CardItemType.COACHED) {
            overrideActiveDate(coached, goals);
            const childItems = goalToOppTransformer(coached);
            childItems?.sort((a, b) => {
                const res = compare(
                    a.opportunityItem.daysActive,
                    b.opportunityItem.daysActive,
                );
                return -res;
            });
            childItems.forEach((item, index) => {
                item.isVisible = index < 3;
            });
            item.isOpened = coached?.length > 0;
            item.isTableExpanded = false;
            item.isVisible = true;
            item.type = CardItemType.COACHED;
            item.isShowMoreEnabled = coached?.length > 3;
            item.hasShowMore = coached?.length > 3;
            item.childItems = childItems;
            item.noDataLabel =
                coached?.length > 0
                    ? null
                    : TranslationKeyMap._10071_THERE_ARE_NO_COACHING_PLANS_CURRENTLY_ACTIVE;
        } else {
            unCoached?.sort((a, b) => {
                const oppA: Opportunity = {
                    isOppActive: a.isOppActive,
                    rankScore: a.rankScore,
                    percentToTarget: a.goal?.currentPctTarget,
                    metricShortName: a.metricShortName,
                };
                const oppB: Opportunity = {
                    isOppActive: b.isOppActive,
                    rankScore: b.rankScore,
                    percentToTarget: b.goal?.currentPctTarget,
                    metricShortName: b.metricShortName,
                };
                return sortOpportunities(oppA, oppB, 'asc');
            });
            item.isOpened = false;
            item.isTableExpanded = false;
            item.isVisible = true;
            item.type = CardItemType.UNCOACHED;
            item.counterLabel =
                unCoached?.length > 0
                    ? unCoached?.length > 3
                        ? '3+'
                        : `${unCoached.length}`
                    : '0';
            item.isShowMoreEnabled = unCoached?.length > 3;
            item.hasShowMore = unCoached?.length > 3;
            item.childItems = goalToOppTransformer(unCoached);
            item.noDataLabel =
                unCoached?.length > 0
                    ? null
                    : TranslationKeyMap._10072_THERE_ARE_NO_UNCOACHED_OPPORTUNITIES;
        }
        items.push(item);
    });

    return items;
}
export const CardItemTypeTranslationMap = {
    [CardItemType.PENDING_COMMITMENTS]:
        TranslationKeyMap._10064_PENDING_COMMITMENTS,
    [CardItemType.COACHED]: TranslationKeyMap._10065_ACTIVE_PLANS,
    [CardItemType.UNCOACHED]: TranslationKeyMap._10066_OTHER_OPPORTUNITIES,
};
